import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { parseJwt } from "../utils/jwt";
import { v4 as uuid } from "uuid";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { installApiErrInterceptor } from "../utils/apiErrorInterceptor";

axios.defaults.baseURL = "/api";

axios.interceptors.request.use(
  (config) => {
    config.headers["X-Trace-Id"] = uuid();
    return config;
  },
  (error) => Promise.reject(error)
);

const refreshClient = axios.create({ baseURL: "/api" });

type AuthCfg = InternalAxiosRequestConfig & {
  __retry?: boolean;
};

const isRefreshRequest = (url?: string): boolean => {
  const path = (url ?? "").split("?")[0];
  return path.endsWith("/token/refresh");
};

const isCredentialAuthRequest = (url?: string): boolean => {
  const path = (url ?? "").split("?")[0];
  return path.includes("/auth/basic") || path.includes("/auth/register");
};

export const useAuth = () => {
  const [user, setUser] = useState<any>();
  const refreshingPromise = useRef<Promise<string> | null>(null);
  const token = useRef<string | null>(localStorage.getItem("token"));
  const refreshToken = useRef<string | null>(localStorage.getItem("refreshToken"));
  const authInterceptorId = useRef<number | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [cookies, , removeCookie, updateCookies] = useCookies(["accessToken"]);

  const persist = useCallback((auth?: string | null, refresh?: string | null) => {
    token.current = auth ?? null;
    refreshToken.current = refresh ?? null;
    if (token.current) localStorage.setItem("token", token.current);
    else localStorage.removeItem("token");
    if (refreshToken.current) localStorage.setItem("refreshToken", refreshToken.current);
    else localStorage.removeItem("refreshToken");
    axios.defaults.headers.common.Authorization = token.current
      ? `Bearer ${token.current}`
      : undefined;
    setUser(token.current ? parseJwt(token.current) : null);
  }, []);

  const forceLogout = useCallback(() => {
    refreshingPromise.current = null;
    persist(null, null);
    navigateRef.current("/");
  }, [persist]);

  const refreshAccessToken = useCallback((): Promise<string> => {
    if (refreshingPromise.current) return refreshingPromise.current;

    const currentRefresh = refreshToken.current ?? localStorage.getItem("refreshToken");
    if (!currentRefresh) {
      return Promise.reject(new Error("missing_refresh_token"));
    }

    refreshingPromise.current = refreshClient
      .post("/token/refresh", null, {
        headers: { Authorization: `Bearer ${currentRefresh}` },
      })
      .then((resp) => {
        const data = typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;
        const auth = typeof data?.auth === "string" ? data.auth : null;
        const nextRefresh =
          typeof data?.refresh === "string" ? data.refresh : currentRefresh;
        if (!auth) {
          throw new Error("invalid_refresh_response");
        }
        persist(auth, nextRefresh);
        return auth;
      })
      .finally(() => {
        refreshingPromise.current = null;
      });

    return refreshingPromise.current;
  }, [persist]);

  const handleAuthError = useCallback(
    (err: AxiosError) => {
      const cfg = err.config as AuthCfg | undefined;
      const status = err.response?.status;
      const url = cfg?.url;

      if (status !== 401 || !cfg) {
        return Promise.reject(err);
      }

      if (isCredentialAuthRequest(url) || isRefreshRequest(url)) {
        return Promise.reject(err);
      }

      if (cfg.__retry) {
        forceLogout();
        return Promise.reject(err);
      }

      const hasRefresh = Boolean(refreshToken.current ?? localStorage.getItem("refreshToken"));
      if (!hasRefresh) {
        forceLogout();
        return Promise.reject(err);
      }

      return refreshAccessToken()
        .then((auth) =>
          axios.request({
            ...cfg,
            headers: {
              ...cfg.headers,
              Authorization: `Bearer ${auth}`,
            },
            __retry: true,
          } as AuthCfg)
        )
        .catch((refreshErr) => {
          forceLogout();
          return Promise.reject(refreshErr);
        });
    },
    [forceLogout, refreshAccessToken]
  );

  const installAuthInterceptor = useCallback(() => {
    if (authInterceptorId.current != null) {
      axios.interceptors.response.eject(authInterceptorId.current);
      authInterceptorId.current = null;
    }
    authInterceptorId.current = axios.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => handleAuthError(err)
    );
    installApiErrInterceptor();
  }, [handleAuthError]);

  const syncFromStorage = useCallback(() => {
    updateCookies();
    if (cookies.accessToken) {
      try {
        const tokens = JSON.parse(atob(cookies.accessToken));
        removeCookie("accessToken");
        cookies.accessToken = null;
        persist(tokens.auth ?? null, tokens.refresh ?? null);
        return;
      } catch {
        removeCookie("accessToken");
        cookies.accessToken = null;
      }
    }
    persist(localStorage.getItem("token"), localStorage.getItem("refreshToken"));
  }, [cookies, persist, removeCookie, updateCookies]);

  const login = useCallback(
    (username: string, password: string) => {
      try {
        const base64 = btoa(`${username}:${password}`);
        return axios
          .post("/auth/basic", null, {
            headers: {
              Authorization: `Basic ${base64}`,
              Accept: "application/json",
            },
          })
          .then((resp: { data: { auth?: string; refresh?: string } }) => {
            const auth = resp.data.auth ?? null;
            const refresh = resp.data.refresh ?? null;
            if (!auth || !refresh) {
              return Promise.reject(new Error("invalid_auth_response"));
            }
            persist(auth, refresh);
            return undefined;
          });
      } catch (e) {
        return Promise.reject(e);
      }
    },
    [persist]
  );

  const logout = useCallback(() => {
    forceLogout();
  }, [forceLogout]);

  const register = useCallback((email: string, name: string, password: string) => {
    return axios.post("/auth/register", {
      email,
      name,
      password,
    });
  }, []);

  const resetPassword = useCallback((email: string) => {
    return axios.post("/auth/reset", { email });
  }, []);

  const resendConfirmEmail = useCallback((email: string) => {
    return axios.post("/auth/resend-confirm-email", { email });
  }, []);

  useEffect(() => {
    syncFromStorage();
    installAuthInterceptor();

    const access = token.current;
    if (!access) return;

    const exp = parseJwt(access)?.exp * 1000;
    if (!exp || exp > Date.now()) return;

    const refresh = refreshToken.current;
    const refreshExp = refresh ? parseJwt(refresh)?.exp * 1000 : 0;
    if (refresh && refreshExp > Date.now()) {
      refreshAccessToken().catch(() => {
        forceLogout();
      });
      return;
    }

    forceLogout();
  }, []);

  return {
    user,
    register,
    login,
    logout,
    resetPassword,
    resendConfirmEmail,
  };
};
